import { useState } from 'react';
import { FaPlus, FaTrash, FaCheck } from 'react-icons/fa6';
import { formatDate } from '../../../../utils/collateralFormatters';
import useLegalCaseHearings, { type AddHearingValue } from '../../../../hooks/useLegalCaseHearings';
import { HearingOutcome, type Hearing } from '../../../../types/LegalCase';
import LegalCaseHearingForm from './LegalCaseHearingForm';
import ConfirmDialog from './ConfirmDialog';

interface LegalCaseHearingsProps {
    caseId: string;
    initialHearings?: Hearing[];
}

// Uma linha de audiência. Extraída para fora do componente (diretriz: sem função dentro de
// função). Selo verde quando realizada (tem heldAt), âmbar quando ainda agendada. Uma
// audiência agendada ganha o atalho "Marcar realizada" (grava heldAt = hoje via update
// parcial); o desfecho detalhado entra depois por edição, se necessário.
const HearingItem = ({
    hearing,
    onMarkHeld,
    onRemove,
    disabled,
}: {
    hearing: Hearing;
    onMarkHeld: () => void;
    onRemove: () => void;
    disabled: boolean;
}) => {
    const badgeClass = hearing.held
        ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
        : 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300';
    return (
        <li className="flex items-start justify-between gap-2 py-2 border-b border-gray-100 dark:border-slate-700 last:border-0">
            <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
                        {hearing.scheduledAt ? formatDate(hearing.scheduledAt) : '—'}
                    </span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${badgeClass}`}>
                        {hearing.held ? 'Realizada' : 'Agendada'}
                    </span>
                    {hearing.outcomeStatus && (
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">{hearing.outcomeStatusFormatted}</span>
                    )}
                </div>
                {hearing.held && hearing.heldAt && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Realizada em {formatDate(hearing.heldAt)}</div>
                )}
                {hearing.description && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-words">{hearing.description}</div>
                )}
                {hearing.outcome && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5 whitespace-pre-wrap break-words">{hearing.outcome}</p>
                )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
                {!hearing.held && (
                    <button
                        type="button"
                        onClick={onMarkHeld}
                        disabled={disabled}
                        title="Marcar como realizada"
                        className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900 rounded-lg transition-colors disabled:opacity-50"
                    >
                        <FaCheck size={11} />
                    </button>
                )}
                <button
                    type="button"
                    onClick={onRemove}
                    disabled={disabled}
                    title="Remover audiência"
                    className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg transition-colors disabled:opacity-50"
                >
                    <FaTrash size={11} />
                </button>
            </div>
        </li>
    );
};

// Data de hoje em ISO (yyyy-mm-dd) para o atalho "marcar realizada".
const todayIso = (): string => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

// Seção "Audiências" do detalhe do caso. Lista as audiências (conciliação, instrução, AGC),
// da mais recente/próxima para a mais antiga. "Realizada" deriva da presença de heldAt. É
// entidade independente da decisão (a audiência pode terminar sem decisão formal). A remoção
// passa por ConfirmDialog (padrão do módulo).
const LegalCaseHearings = ({ caseId, initialHearings }: LegalCaseHearingsProps) => {
    const hearings = useLegalCaseHearings(caseId, initialHearings);
    const [showForm, setShowForm] = useState(false);
    const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

    const openForm = () => {
        hearings.clearErrors();
        setShowForm(true);
    };

    const closeForm = () => {
        hearings.clearErrors();
        setShowForm(false);
    };

    const submitHearing = async (value: AddHearingValue): Promise<boolean> => {
        const ok = await hearings.addHearing(value);
        if (ok) closeForm();
        return ok;
    };

    const markHeld = (hearingId: string) =>
        hearings.updateHearing(hearingId, { heldAt: todayIso(), outcomeStatus: HearingOutcome.HELD });

    const confirmRemoval = async () => {
        if (!pendingRemoval) return;
        await hearings.removeHearing(pendingRemoval);
        setPendingRemoval(null);
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Audiências
                </h4>
                {!showForm && (
                    <button
                        type="button"
                        onClick={openForm}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                    >
                        <FaPlus size={10} /> Audiência
                    </button>
                )}
            </div>

            {hearings.hearings.length === 0 && !showForm && (
                <p className="text-sm text-gray-400 dark:text-gray-500">Nenhuma audiência registrada</p>
            )}

            {hearings.hearings.length > 0 && (
                <ul>
                    {hearings.hearings.map((hearing) => (
                        <HearingItem
                            key={hearing.id}
                            hearing={hearing}
                            onMarkHeld={() => markHeld(hearing.id)}
                            onRemove={() => setPendingRemoval(hearing.id)}
                            disabled={hearings.isSaving}
                        />
                    ))}
                </ul>
            )}

            {showForm && (
                <LegalCaseHearingForm
                    outcomeOptions={hearings.outcomeOptions}
                    errorFields={hearings.errorFields}
                    isSaving={hearings.isSaving}
                    onSubmit={submitHearing}
                    onCancel={closeForm}
                />
            )}

            <ConfirmDialog
                open={pendingRemoval !== null}
                title="Remover audiência"
                message="Remover esta audiência do caso? Esta ação não pode ser desfeita."
                isBusy={hearings.isSaving}
                onConfirm={confirmRemoval}
                onCancel={() => setPendingRemoval(null)}
            />
        </div>
    );
};

export default LegalCaseHearings;
